import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { createElement } from 'react';
import FocusLock from 'react-focus-lock';
import { useId } from 'react-id-generator';
import styled from 'styled-components';
import { KEYS } from '../../constants/keys.js';
import { useVisibility } from '../../hooks/use_is_visible.js';
import { useTranslations } from '../../hooks/use_translations.js';
import { spacing4x, spacing2x } from '../../styles/spacing.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { escape } from '../../utils/util.js';
import { Alert, roleMap } from '../Alert/Alert.js';
import { Button } from '../Button/Button.js';
import { IconClose } from '../Icons/IconClose.js';
import { LinkButton } from '../LinkButton/LinkButton.js';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var _excluded = ["autoDismiss", "content", "CTAs", "dangerouslyUnescaped", "destroyable", "id", "onClose", "onDestroy", "onDismiss", "permanent", "variant"];
var TOAST_TIMEOUT = 5e3;
var toastWidth = "304px";
var Body = styled.div.withConfig({
  displayName: "Toast__Body",
  componentId: "sc-4y7v0k-0"
})(["&&{margin-block-start:", ";}"], spacing4x);
var Content = styled.div.withConfig({
  displayName: "Toast__Content",
  componentId: "sc-4y7v0k-1"
})(["&&{word-break:break-word;}"]);
var CTAContainer = styled.div.withConfig({
  displayName: "Toast__CTAContainer",
  componentId: "sc-4y7v0k-2"
})(["&&{margin-block-start:", ";display:flex;}"], spacing4x);
var StyledAlert = styled(Alert).withConfig({
  displayName: "Toast__StyledAlert",
  componentId: "sc-4y7v0k-3"
})(["&&{position:relative;width:", ";z-index:9999;}"], toastWidth);
var StyledButton = styled(Button).withConfig({
  displayName: "Toast__StyledButton",
  componentId: "sc-4y7v0k-4"
})(["&&{position:absolute;right:0;top:0;}[dir='rtl'] &&{right:unset;left:0;}"]);
var Title = styled.span.withConfig({
  displayName: "Toast__Title",
  componentId: "sc-4y7v0k-5"
})(["&&{", ";font-size:16px;font-weight:600;line-height:24px;margin-block-end:", ";}"], function (props) {
  return "color: ".concat(props.theme.neutralColors.smoke);
}, spacing4x);
var ToastContainer = styled.div.withConfig({
  displayName: "Toast__ToastContainer",
  componentId: "sc-4y7v0k-6"
})(["&&{margin-block-start:", ";width:", ";}"], spacing4x, toastWidth);
var DestroyBtn = styled(LinkButton).withConfig({
  displayName: "Toast__DestroyBtn",
  componentId: "sc-4y7v0k-7"
})(["&&{margin-block-start:", ";}"], spacing2x);
var Toast = /*#__PURE__*/React.forwardRef(function Toast2(props, ref) {
  var _props$autoDismiss = props.autoDismiss,
    autoDismiss = _props$autoDismiss === void 0 ? true : _props$autoDismiss,
    content = props.content,
    CTAs = props.CTAs,
    dangerouslyUnescaped = props.dangerouslyUnescaped,
    destroyable = props.destroyable,
    id = props.id,
    onClose = props.onClose,
    onDestroy = props.onDestroy,
    onDismiss = props.onDismiss,
    permanent = props.permanent,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? "info" : _props$variant,
    rest = _objectWithoutProperties(props, _excluded);
  var _useThemeContext = useThemeContext(),
    localization = _useThemeContext.localization;
  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
    t = _useTranslations.t;
  if (process.env.NODE_ENV !== "production" && Array.isArray(CTAs) && CTAs.length > 2) {
    throw new Error("Toast prop CTAs can only have a max length of 2");
  }
  var _useVisibility = useVisibility(true),
    _useVisibility2 = _slicedToArray(_useVisibility, 2),
    isVisible = _useVisibility2[0],
    inspectedElement = _useVisibility2[1];
  var timeOutId = React.useRef(1);
  var destroyRef = React.useRef(null);
  var _useId = useId(1, "toastContent-"),
    _useId2 = _slicedToArray(_useId, 1),
    contentId = _useId2[0];
  var _useId3 = useId(1, "toastTitle-"),
    _useId4 = _slicedToArray(_useId3, 1),
    titleId = _useId4[0];
  var isInteractive = CTAs && CTAs.length > 0 || destroyable;
  var hasAutoDismiss = !permanent && !isInteractive && autoDismiss;
  var skipTimer = hasAutoDismiss && !isVisible;
  function handleAutoDismissExpired(e) {
    e === null || e === void 0 ? void 0 : e.preventDefault();
    if (onDismiss) onDismiss(id);
    onClose(id);
  }
  var startAutoDismissTimer = React.useCallback(function () {
    if (!hasAutoDismiss || skipTimer) return;
    timeOutId.current = window.setTimeout(handleAutoDismissExpired, TOAST_TIMEOUT);
  }, [skipTimer]);
  var stopAutoDismissTimer = React.useCallback(function () {
    if (!timeOutId.current) return;
    clearTimeout(timeOutId.current);
  }, []);
  React.useEffect(function () {
    startAutoDismissTimer();
    return stopAutoDismissTimer;
  }, [startAutoDismissTimer, stopAutoDismissTimer]);
  function handleCTAClick(cta, e) {
    e.stopPropagation();
    cta(e);
    onClose(id);
  }
  function handleOnClose(e) {
    e === null || e === void 0 ? void 0 : e.preventDefault();
    onClose(id);
  }
  function handleOnDestroy(e) {
    e.preventDefault();
    onDestroy();
  }
  function renderCTAs() {
    if (!(CTAs !== null && CTAs !== void 0 && CTAs.length)) return null;
    var renderedCTAs = CTAs.map(function (CTA, key) {
      var _onClick = CTA.onClick,
        renderLink = CTA.renderLink,
        title = CTA.title,
        to = CTA.to;
      var toastLinkStyle = {
        fontWeight: 600,
        lineHeight: "16px",
        maxWidth: "calc(100% - 64px)",
        minWidth: "0%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      };
      if (renderLink) return renderLink(_objectSpread(_objectSpread({
        style: toastLinkStyle
      }, CTA), {}, {
        key: key,
        onClick: function onClick(e) {
          return handleCTAClick(_onClick, e);
        }
      }));
      if (to) return /*#__PURE__*/createElement("a", _objectSpread(_objectSpread({}, CTA), {}, {
        style: toastLinkStyle,
        onClick: function onClick(e) {
          return handleCTAClick(_onClick, e);
        },
        key: key,
        href: to
      }), title);
      return /*#__PURE__*/jsx(LinkButton, {
        style: toastLinkStyle,
        onClick: function onClick(e) {
          return handleCTAClick(_onClick, e);
        },
        label: title
      }, key);
    });
    if (renderedCTAs.length > 1) {
      renderedCTAs = [renderedCTAs[0], /*#__PURE__*/jsx("div", {
        role: "none",
        style: {
          flex: "1 1 ".concat(spacing4x)
        }
      }, "spacer"), renderedCTAs[1]];
    }
    return /*#__PURE__*/jsx(CTAContainer, {
      children: renderedCTAs
    });
  }
  function handleToastKeyDown(e) {
    switch (e.key) {
      case KEYS.ESCAPE:
        {
          handleOnClose(e);
          e.stopPropagation();
        }
    }
  }
  return /*#__PURE__*/jsx(ToastContainer, {
    ref: inspectedElement,
    children: /*#__PURE__*/jsx(FocusLock, {
      autoFocus: false,
      disabled: !isInteractive,
      lockProps: {
        "aria-labelledby": isInteractive ? titleId : void 0,
        "aria-describedby": isInteractive ? contentId : void 0,
        "aria-modal": isInteractive ? true : void 0,
        role: isInteractive ? "alertdialog" : void 0,
        onMouseEnter: stopAutoDismissTimer,
        onMouseLeave: startAutoDismissTimer
      },
      returnFocus: true,
      children: /*#__PURE__*/jsxs(StyledAlert, _objectSpread(_objectSpread({}, rest), {}, {
        onKeyDown: handleToastKeyDown,
        type: "toast",
        variant: variant,
        ref: ref,
        children: [!isInteractive && /*#__PURE__*/jsx(VisuallyHidden, {
          children: "".concat(roleMap[variant], " ").concat(t("Toast.srOnly"), ".")
        }), /*#__PURE__*/jsx(Title, {
          id: titleId,
          children: t("Toast.titleMap.".concat(variant))
        }), /*#__PURE__*/jsxs(Body, {
          children: [content && /*#__PURE__*/jsx(Content, {
            id: isInteractive ? contentId : void 0,
            dangerouslySetInnerHTML: {
              __html: dangerouslyUnescaped ? content : escape(content)
            }
          }), renderCTAs(), destroyable && onDestroy && /*#__PURE__*/jsx(DestroyBtn, {
            ref: destroyRef,
            onClick: handleOnDestroy,
            label: t("Toast.destroy")
          })]
        }), /*#__PURE__*/jsx(StyledButton, {
          hideLabel: true,
          label: t("Toast.close"),
          onClick: handleOnClose,
          startAdornment: IconClose,
          variant: "ghost"
        })]
      }))
    })
  });
});
Toast.displayName = "Toast";

export { Toast };
